import type { ApiClientErrorCode, DecorKind, DecorMatchResponseItem } from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { CirclePickerOverlay } from '@/components/PhotoTools/CirclePickerOverlay';
import { RadiusSlider } from '@/components/PhotoTools/RadiusSlider';
import { MatchResults } from './MatchResults';

/**
 * Presenter Экрана 2.2.3 (Подбор декора по фото). См. ADR-0005, ADR-0008.
 * Чистая функция от props к JSX. Никаких useEffect/fetch/useNavigate.
 *
 * Internal states (P-021 + ADR-0008 §8 + ADR-0042):
 *   - noImage:      2 функции загрузки (файл JPG/PNG/PDF / камера)
 *   - pdfPageSelect: многостраничный PDF — превью + степпер выбора страницы
 *   - imageLoaded:  фото + 2 mode-кнопки (average / area)
 *   - pickerActive: фото + SVG circle overlay + slider + submit-кнопка
 *   - loading:      submit отправлен / PDF рендерится, ждём
 *   - results:      5 карточек MatchResults + «Загрузить другое фото»
 *   - error:        fixed text
 *
 * Координаты круга — image-pixel space (передаются из Container'а как `cx`,
 * `cy`, `radius` уже в image-px). `radiusPercent` дополнительно для UI slider.
 */
export type PhotoPickerState =
  | 'noImage'
  | 'pdfPageSelect'
  | 'imageLoaded'
  | 'pickerActive'
  | 'loading'
  | 'results'
  | 'error';

export interface PhotoPickerViewProps {
  kind: DecorKind;
  state: PhotoPickerState;
  /** `URL.createObjectURL(file)`. Container revoke'нет на unmount/reset. */
  imageUrl: string | undefined;
  imageWidth: number;
  imageHeight: number;
  /** Image-pixel space (см. ADR-0008 §1). */
  cx: number;
  cy: number;
  radius: number;
  /** Percent от minSide для UI slider (5..45 per ADR-0008 §4). */
  radiusPercent: number;
  /** Многостраничный PDF: текущая страница (1-based) + всего. */
  pdfPage: number;
  pdfNumPages: number;
  matchItems?: readonly DecorMatchResponseItem[];
  /** Фото-2 (PR-DATA4): signed-URL по `primaryPhotoKey` результатов подбора. */
  matchImageUrls?: Record<string, string>;
  error?: { code: ApiClientErrorCode; message: string };

  onFileSelect: (file: File) => void;
  onAverageMatch: () => void;
  onActivatePicker: () => void;
  onAreaMatch: () => void;
  onBackToImageLoaded: () => void;
  onCircleMove: (cx: number, cy: number) => void;
  onRadiusChange: (percent: number) => void;
  onPdfPrevPage: () => void;
  onPdfNextPage: () => void;
  onPdfSelectPage: () => void;
  onResetImage: () => void;
  onItemClick: (globalId: string) => void;
}

export function PhotoPickerView(props: PhotoPickerViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Подбор по фото" />
      <section className="flex-1 p-6 overflow-y-auto">
        <StateView {...props} />
      </section>
      <BottomBar />
    </main>
  );
}

function StateView(props: PhotoPickerViewProps) {
  switch (props.state) {
    case 'noImage':
      return <NoImageState onFileSelect={props.onFileSelect} />;
    case 'pdfPageSelect':
      return (
        <PdfPageSelectState
          imageUrl={props.imageUrl ?? ''}
          pdfPage={props.pdfPage}
          pdfNumPages={props.pdfNumPages}
          onPdfPrevPage={props.onPdfPrevPage}
          onPdfNextPage={props.onPdfNextPage}
          onPdfSelectPage={props.onPdfSelectPage}
          onResetImage={props.onResetImage}
        />
      );
    case 'imageLoaded':
      return (
        <ImageLoadedState
          imageUrl={props.imageUrl ?? ''}
          onAverageMatch={props.onAverageMatch}
          onActivatePicker={props.onActivatePicker}
          onResetImage={props.onResetImage}
        />
      );
    case 'pickerActive':
      return (
        <PickerActiveState
          imageUrl={props.imageUrl ?? ''}
          imageWidth={props.imageWidth}
          imageHeight={props.imageHeight}
          cx={props.cx}
          cy={props.cy}
          radius={props.radius}
          radiusPercent={props.radiusPercent}
          onCircleMove={props.onCircleMove}
          onRadiusChange={props.onRadiusChange}
          onAreaMatch={props.onAreaMatch}
          onBackToImageLoaded={props.onBackToImageLoaded}
        />
      );
    case 'loading':
      return <LoadingState />;
    case 'results':
      return (
        <ResultsState
          items={props.matchItems ?? []}
          imageUrls={props.matchImageUrls}
          onItemClick={props.onItemClick}
          onResetImage={props.onResetImage}
        />
      );
    case 'error':
      return <ErrorState message={props.error?.message ?? ''} onResetImage={props.onResetImage} />;
  }
}

function NoImageState({ onFileSelect }: { onFileSelect: (file: File) => void }) {
  // Общий обработчик: берём первый файл, сбрасываем value (повторный выбор того
  // же файла снова триггерит onChange).
  const handle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) onFileSelect(f);
    e.target.value = '';
  };
  return (
    <div className="flex flex-col gap-5">
      {/* Функция 1 — файл (JPG / PNG / PDF). aria-label сохранён: на нём завязаны
          e2e decors-photo-match / decors-p009 (`getByLabel`). */}
      <div className="bg-card rounded-xl border-[1.5px] border-accent p-5 flex flex-col gap-3">
        <p className="text-ink text-[16px] m-0 leading-normal">
          Загрузи Фото/Дизайн (JPG, PNG, PDF)
        </p>
        <label
          className={[
            'w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink',
            'font-bold text-[18px] py-3 flex items-center justify-center cursor-pointer',
            'focus-within:ring-2 focus-within:ring-cta active:opacity-90',
          ].join(' ')}
        >
          Выберите файл
          <input
            type="file"
            accept="image/*,application/pdf"
            aria-label="Выбрать файл с фото"
            onChange={handle}
            className="sr-only"
          />
        </label>
      </div>

      {/* Функция 2 — камера (capture). На ПК откроет файл-диалог (fallback). */}
      <div className="bg-card rounded-xl border-[1.5px] border-accent p-5 flex flex-col gap-3">
        <p className="text-ink text-[16px] m-0 leading-normal">Сделай фото и загрузи</p>
        <label
          className={[
            'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
            'font-medium text-[16px] py-3 flex items-center justify-center cursor-pointer',
            'focus-within:ring-2 focus-within:ring-cta active:opacity-90',
          ].join(' ')}
        >
          Камера
          <input
            type="file"
            accept="image/*"
            capture="environment"
            aria-label="Сделать фото камерой"
            onChange={handle}
            className="sr-only"
          />
        </label>
      </div>
    </div>
  );
}

function PdfPageSelectState({
  imageUrl,
  pdfPage,
  pdfNumPages,
  onPdfPrevPage,
  onPdfNextPage,
  onPdfSelectPage,
  onResetImage,
}: {
  imageUrl: string;
  pdfPage: number;
  pdfNumPages: number;
  onPdfPrevPage: () => void;
  onPdfNextPage: () => void;
  onPdfSelectPage: () => void;
  onResetImage: () => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      <div className="w-full max-h-[50vh] overflow-hidden rounded-xl bg-icon-bg flex items-center justify-center">
        <img
          src={imageUrl}
          alt={`Страница ${pdfPage} из ${pdfNumPages}`}
          className="w-full h-auto object-contain max-h-[50vh]"
        />
      </div>
      <p className="text-ink text-center text-[16px] m-0">
        Страница {pdfPage} из {pdfNumPages}
      </p>
      {/* Навигация вертикальным списком (P-«кнопки только вертикально»). */}
      <button
        type="button"
        onClick={onPdfPrevPage}
        disabled={pdfPage <= 1}
        className={[
          'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
          'text-[16px] py-3 disabled:opacity-50',
          'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
        ].join(' ')}
      >
        ← Предыдущая страница
      </button>
      <button
        type="button"
        onClick={onPdfNextPage}
        disabled={pdfPage >= pdfNumPages}
        className={[
          'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
          'text-[16px] py-3 disabled:opacity-50',
          'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
        ].join(' ')}
      >
        Следующая страница →
      </button>
      <button
        type="button"
        onClick={onPdfSelectPage}
        className={[
          'w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink',
          'font-bold text-[18px] py-3',
          'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
        ].join(' ')}
      >
        Выбрать эту страницу
      </button>
      <button
        type="button"
        onClick={onResetImage}
        className="w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink text-[14px] py-3"
      >
        Загрузить другой файл
      </button>
    </div>
  );
}

function ImageLoadedState({
  imageUrl,
  onAverageMatch,
  onActivatePicker,
  onResetImage,
}: {
  imageUrl: string;
  onAverageMatch: () => void;
  onActivatePicker: () => void;
  onResetImage: () => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      <div className="w-full max-h-[50vh] overflow-hidden rounded-xl bg-icon-bg flex items-center justify-center">
        <img
          src={imageUrl}
          alt="Загруженное фото"
          className="w-full h-auto object-contain max-h-[50vh]"
        />
      </div>
      <button
        type="button"
        onClick={onAverageMatch}
        className={[
          'w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink',
          'font-bold text-[18px] py-3',
          'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
        ].join(' ')}
      >
        Подобрать декор
      </button>
      <button
        type="button"
        onClick={onActivatePicker}
        className={[
          'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
          'font-medium text-[16px] py-3',
          'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
        ].join(' ')}
      >
        Подобрать декор для конкретной детали
      </button>
      <button
        type="button"
        onClick={onResetImage}
        className="w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink text-[14px] py-3"
      >
        Загрузить другое фото
      </button>
    </div>
  );
}

function PickerActiveState({
  imageUrl,
  imageWidth,
  imageHeight,
  cx,
  cy,
  radius,
  radiusPercent,
  onCircleMove,
  onRadiusChange,
  onAreaMatch,
  onBackToImageLoaded,
}: {
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  cx: number;
  cy: number;
  radius: number;
  radiusPercent: number;
  onCircleMove: (cx: number, cy: number) => void;
  onRadiusChange: (percent: number) => void;
  onAreaMatch: () => void;
  onBackToImageLoaded: () => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      <div className="relative w-full max-h-[50vh] overflow-hidden rounded-xl bg-icon-bg">
        <img
          src={imageUrl}
          alt="Загруженное фото"
          className="w-full h-auto object-contain max-h-[50vh]"
        />
        <CirclePickerOverlay
          imageWidth={imageWidth}
          imageHeight={imageHeight}
          cx={cx}
          cy={cy}
          radius={radius}
          onMove={onCircleMove}
        />
      </div>
      <RadiusSlider radiusPercent={radiusPercent} onRadiusChange={onRadiusChange} />
      <button
        type="button"
        onClick={onAreaMatch}
        className={[
          'w-full min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink',
          'font-bold text-[18px] py-3',
          'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
        ].join(' ')}
      >
        Подобрать декоры
      </button>
      <button
        type="button"
        onClick={onBackToImageLoaded}
        className="w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink text-[16px] py-3"
      >
        ← Изменить режим
      </button>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-center">
      <p className="text-muted text-[18px] m-0">Ищем похожие декоры…</p>
    </div>
  );
}

function ResultsState({
  items,
  imageUrls,
  onItemClick,
  onResetImage,
}: {
  items: readonly DecorMatchResponseItem[];
  imageUrls?: Record<string, string>;
  onItemClick: (globalId: string) => void;
  onResetImage: () => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      <MatchResults items={items} imageUrls={imageUrls} onItemClick={onItemClick} />
      <button
        type="button"
        onClick={onResetImage}
        className="w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink text-[16px] py-3"
      >
        Загрузить другое фото
      </button>
    </div>
  );
}

function ErrorState({ message, onResetImage }: { message: string; onResetImage: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-error text-[18px] max-w-[280px] m-0 leading-normal">
        Не удалось получить ответ. Проверьте интернет и попробуйте снова.
      </p>
      {message && <p className="text-muted text-sm">{message}</p>}
      <button
        type="button"
        onClick={onResetImage}
        className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
      >
        Загрузить другое фото
      </button>
    </div>
  );
}
